import SupplierFooter from '@/components/Suppliers/SupplierFooter';
import { useSupplierFormContext } from '@/components/Suppliers/SupplierFormProvider';
import {
  DefaultRouteTabs,
  InfiniteTable,
  MultipleContactModal,
  SaveButton,
} from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import {
  useCreateSupplierContacts,
  useSupplierContactDetailRemove,
  useSupplierContactRemove,
  useSupplierContacts,
  useUpdateSupplierContacts,
} from '@/core/domain/people/SupplierContact/SupplierContact.hook';
import { SupplierContacts } from '@/core/domain/people/SupplierContact/SupplierContact.types';
import { useDisclosure } from '@/hooks';
import { useContactColumns } from '@/hooks/useContactColumns';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import supplierValidationTabs from '@/utils/functions/supplierValidationTabs';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';
import { DateTimeFormat } from '@/utils/moment';
import { Button, Divider, Form, Modal, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

export default function SupplierContactsPage({ route }: PageProps) {
  const intl = useIntl().formatMessage;
  const [form] = Form.useForm();
  const { isLoading, supplierId, disabled, handleSubmit, supplier } =
    useSupplierFormContext();
  const { columns: contactColumns } = useContactColumns<SupplierContacts>();

  const isSupplierGlobal = !supplier?.matrixId;

  const { setParamLabel } = useBreadcrumbs();
  const [modalVisible, modalActions] = useDisclosure(false);

  const supplierContacts = useSupplierContacts(Number(supplierId));
  const supplierContactCreate = useCreateSupplierContacts();
  const supplierContactEdit = useUpdateSupplierContacts();
  const supplierContactRemove = useSupplierContactRemove();
  const supplierRemoveDetail = useSupplierContactDetailRemove();

  useEffect(() => {
    if (supplier) {
      setParamLabel(':supplierId', supplier.name);
    }
  }, [supplier]);

  useEffect(() => {
    if (supplierContacts.data?.length && modalVisible) {
      const contactUpdated = supplierContacts.data.find(
        (contact) => contact.id === form.getFieldValue('contactId'),
      );

      if (contactUpdated) {
        handleContactSelect(contactUpdated);
      }
    }
  }, [supplierContacts.data, modalVisible]);

  function handleContactSelect(supplierContact: SupplierContacts) {
    form.setFieldsValue({
      ...supplierContact,
      detailList: supplierContact.detailList,
      contactId: supplierContact.id,
      birthDate: supplierContact.birthDate
        ? moment(supplierContact.birthDate)
        : undefined,
    });
  }

  async function handleSave() {
    try {
      const { contactId, ...values } = await form.validateFields();

      const obj = {
        ...values,
        detailList: removeMaskDetailList(values.detailList),
        birthDate: values.birthDate
          ? values.birthDate.format(DateTimeFormat)
          : undefined,
      };

      if (contactId) {
        await supplierContactEdit.mutateAsync({
          obj,
          supplierId: Number(supplierId),
          id: contactId,
        });
      } else {
        await supplierContactCreate.mutateAsync({
          obj,
          supplierId: Number(supplierId),
        });
      }

      handleCancel();
    } catch {}
  }

  function handleContactRemove(supplierContact: SupplierContacts) {
    Modal.confirm({
      title: intl({ id: 'suppliers.contacts.remove' }),
      content: `${intl({ id: 'suppliers.contacts.remove.alert' })} ${
        supplierContact.name
      }?`,
      onOk: () =>
        supplierContactRemove.mutateAsync({
          supplierId: supplierContact.supplierId,
          contactId: supplierContact.id,
        }),
      okText: intl({ id: 'commons.form.button.remove' }),
    });
  }

  async function handleRemoveContactDetail(detailId: number) {
    await supplierRemoveDetail.mutateAsync({
      supplierId: Number(supplierId),
      detailId: detailId,
    });

    supplierContacts.refetch();
  }

  const handleCancel = () => {
    modalActions.close();
    form.resetFields();
  };

  const columns: ColumnsType<SupplierContacts> = [
    ...contactColumns,
    {
      key: 'action',
      title: intl({ id: 'commons.list.header.action' }),
      align: 'center',
      fixed: 'right',
      width: 50,
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                handleContactSelect(row);
                modalActions.open();
              },
            }}
            disabled={!hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleContactRemove(row)}
            disabled={
              isSupplierGlobal ||
              !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={supplierValidationTabs(route, supplier)}
      baseKey={`${supplierId}`}
      basePath={`/app/registry/suppliers/${supplierId}`}
      extra={
        <SpaceForButton>
          <Button
            type="primary"
            block
            onClick={modalActions.open}
            disabled={
              !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT]) ||
              isSupplierGlobal
            }
          >
            {intl({ id: 'commons.entity.add' })}
          </Button>
          <SaveButton
            block
            loading={isLoading}
            onClick={handleSubmit}
            disabled={
              isSupplierGlobal ||
              disabled ||
              !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])
            }
          >
            {intl({ id: 'commons.entity.save' })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet title={intl({ id: route.name })} />

      <InfiniteTable
        dataSource={supplierContacts.data}
        loading={supplierContacts.isLoading}
        columns={columns}
        rowKey={(row) => row.id}
        pagination={false}
      />

      <SupplierFooter disabled={disabled} />

      <MultipleContactModal
        destroyOnClose
        confirmLoading={
          supplierContactCreate.isLoading || supplierContactEdit.isLoading
        }
        onDetailRemove={handleRemoveContactDetail}
        open={modalVisible}
        onCancel={handleCancel}
        onOk={handleSave}
        form={form}
        personType={supplier?.cnpj ? 'LegalPerson' : 'NaturalPerson'}
        loading={
          supplierContactCreate.isLoading || supplierContactEdit.isLoading
        }
        width={700}
        fieldsDisabled={!supplier?.matrixId}
      />
    </DefaultRouteTabs>
  );
}
